import type { TApplicationStatus } from '@doutorsim/shared/applications'
import { useMemo } from 'react'

import {
  getApplicationColumns,
  type LinkBuilder,
} from '@/features/_columns/application-table-columns'
import { useFacilityMap } from '@/features/_hooks/use-facility-map'
import { usePaginatedApplicationsQuery } from '@/features/_hooks/use-paginated-applications-query'
import type { TUserType } from '../_lib/auth-client.types'

import { DataTable } from './data-table'
import { DataTablePagination } from './data-table-pagination'
import { LoadingDataTable } from './loading-data-table'

interface PersonsTableProps {
  mode: 'staff' | 'company' | 'facility'
  facilityId?: string
  companyId?: string
  userType: TUserType
  showFacilityColumn?: boolean
  search?: string
  status?: TApplicationStatus[]
  startDate?: string
  endDate?: string
  filterFacilityIds?: string[]
  linkBuilder?: LinkBuilder
}

export const PersonsTable = ({
  mode,
  facilityId,
  companyId,
  userType,
  showFacilityColumn = false,
  status,
  startDate,
  endDate,
  search,
  filterFacilityIds,
  linkBuilder,
}: PersonsTableProps) => {
  const facilityMap = useFacilityMap(mode, companyId)

  const columns = useMemo(
    () => getApplicationColumns({ showFacility: showFacilityColumn, userType, linkBuilder }),
    [showFacilityColumn, userType, linkBuilder],
  )

  const {
    data: rawData,
    isFetching,
    currentPage,
    totalPages,
    totalCount,
    canGoNext,
    canGoPrevious,
    goToNextPage,
    goToPreviousPage,
  } = usePaginatedApplicationsQuery({
    mode,
    companyId,
    facilityId,
    search,
    status,
    startDate,
    endDate,
    filterFacilityIds,
  })

  const data = rawData.map((application) => ({
    ...application,
    facility: facilityMap?.get(application.facilityId) ?? null,
  }))

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {isFetching ? (
        <LoadingDataTable columns={columns} rows={rawData.length || undefined} />
      ) : (
        <DataTable columns={columns} data={data} />
      )}
      <DataTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
      />
    </div>
  )
}
